import { useEffect, useState } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { pushSupported, pushPermission, isPushEnabled, enablePush, disablePush, sendTestPush } from '../api/push';

// Profil-Karte (Fork aus powerCONTACT): Push-Benachrichtigungen fuer geteilte Chats und neue Dokumente im Eingang.
// Auf iOS funktioniert Web-Push NUR in der installierten PWA (Home-Bildschirm), nicht im
// Safari-Tab — daher der Installationshinweis.
export default function NotificationCard() {
  const [enabled, setEnabled] = useState(false);
  const [perm, setPerm] = useState(pushPermission());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => { isPushEnabled().then(setEnabled); }, []);

  const standalone = window.matchMedia('(display-mode: standalone)').matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true;

  const toggle = async () => {
    setBusy(true); setMsg(null);
    try {
      if (enabled) { await disablePush(); setEnabled(false); setMsg('Benachrichtigungen deaktiviert.'); }
      else {
        const ok = await enablePush();
        setEnabled(ok); setPerm(pushPermission());
        setMsg(ok ? 'Benachrichtigungen aktiviert ✅' : 'Berechtigung wurde abgelehnt.');
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Fehler beim Umschalten.');
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setMsg(null);
    try {
      const n = await sendTestPush();
      setMsg(n > 0 ? 'Test-Benachrichtigung gesendet — gleich sollte sie erscheinen.' : 'Kein registriertes Gerät gefunden.');
    } catch { setMsg('Test-Versand fehlgeschlagen.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start gap-3">
        <BellIcon className="h-6 w-6 flex-shrink-0 text-ps-orange-500" />
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900">Benachrichtigungen</h2>
          <p className="mt-1 text-sm text-gray-600">
            Push-Benachrichtigung bei <b>geteilten Chats</b> und <b>neuen Dokumenten im Eingang</b> — direkt auf dem Home-Bildschirm,
            auch wenn die App geschlossen ist.
          </p>

          {!pushSupported() ? (
            <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              Dein Browser unterstützt keine Push-Benachrichtigungen.
              {!standalone && ' Auf dem iPhone: die App über „Teilen → Zum Home-Bildschirm" installieren und die App von dort öffnen, dann hier aktivieren.'}
            </p>
          ) : perm === 'denied' ? (
            <p className="mt-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700">
              Benachrichtigungen sind im Browser/in den Geräte-Einstellungen blockiert. Bitte dort für die App erlauben und neu laden.
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button onClick={toggle} disabled={busy}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white transition disabled:opacity-50 ${enabled ? 'bg-ps-dark-500 hover:bg-ps-dark-600' : 'bg-ps-orange-500 hover:bg-ps-orange-600'}`}>
                {busy ? '…' : enabled ? 'Benachrichtigungen deaktivieren' : 'Benachrichtigungen aktivieren'}
              </button>
              {enabled && (
                <button onClick={test} disabled={busy}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                  Test senden
                </button>
              )}
            </div>
          )}

          {msg && <p className="mt-2 text-xs text-gray-500">{msg}</p>}
          {!standalone && pushSupported() && (
            <p className="mt-2 text-[11px] text-gray-400">
              Tipp: Als Home-Bildschirm-App (PWA) installiert funktionieren Benachrichtigungen am zuverlässigsten.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
